import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";

const METRIC_CONFIG = [
  { key: "total", label: "Total Jobs", icon: "bi-briefcase-fill", color: "#0d6efd", bg: "#e7f1ff" },
  { key: "active", label: "Active Jobs", icon: "bi-check-circle-fill", color: "#198754", bg: "#e6f4ed" },
  { key: "closed", label: "Closed Jobs", icon: "bi-x-circle-fill", color: "#6c757d", bg: "#f1f3f5" },
  { key: "applicants", label: "Total Applicants", icon: "bi-people-fill", color: "#6f42c1", bg: "#f0ebff" },
  { key: "shortlisted", label: "Shortlisted", icon: "bi-star-fill", color: "#fd7e14", bg: "#fff3e0" },
];

const STATUS_STYLES = {
  active: { bg: "#e6f4ed", color: "#198754" },
  closed: { bg: "#f1f3f5", color: "#6c757d" },
  draft: { bg: "#fff3e0", color: "#fd7e14" },
  paused: { bg: "#e7f1ff", color: "#0d6efd" },
};

const RecruiterDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState({ total: 0, shortlisted: 0 });
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const [jobResponse, summaryResponse, profileResponse, subscriptionResponse] = await Promise.all([
        axiosInstance.get("/job-portal/recruiter/jobs?limit=100", authConfig),
        axiosInstance.get("/job-portal/applications/summary", authConfig),
        axiosInstance.post("/auth-user", { id: authConfig.headers["x-auth-user-id"] }),
        axiosInstance.get("/job-portal/subscription/me", authConfig),
      ]);
      setJobs(jobResponse.data?.data?.jobs || []);
      setSummary(summaryResponse.data?.data?.summary || { total: 0, shortlisted: 0 });
      setProfile(profileResponse.data?.existingUser || null);
      setSubscription(subscriptionResponse.data?.data?.subscription || null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  const metrics = useMemo(() => {
    const totalJobs = jobs.length;
    const activeJobs = jobs.filter((j) => j.jobStatus === "active").length;
    const closedJobs = jobs.filter((j) => j.jobStatus === "closed").length;
    return {
      total: totalJobs,
      active: activeJobs,
      closed: closedJobs,
      applicants: summary.total || 0,
      shortlisted: summary.shortlisted || 0,
    };
  }, [jobs, summary]);

  const recentJobs = jobs.slice(0, 5);

  const industryList = Array.isArray(profile?.NatureOfBusiness)
    ? profile.NatureOfBusiness.join(", ")
    : profile?.NatureOfBusiness || null;

  return (
    <>
      <style>{`
        .dash-metric-card {
          border-radius: 16px; padding: 20px;
          transition: transform 0.18s, box-shadow 0.18s; cursor: default;
        }
        .dash-metric-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.09) !important; }
        .dash-metric-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 18px; margin-bottom: 12px; }
        .dash-metric-val { font-size: 28px; font-weight: 700; line-height: 1; margin-bottom: 4px; }
        .dash-metric-label { font-size: 11px; color: #868e96; font-weight: 600; text-transform: uppercase; letter-spacing: 0.6px; }
        .section-card { border-radius: 16px; border: 1px solid #e9ecef; box-shadow: 0 2px 12px rgba(0,0,0,0.05); }
        .section-card .card-body { padding: 24px; }
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 14px; border-bottom: 1px solid #f1f3f5; }
        .section-title { font-size: 15px; font-weight: 700; margin: 0; }
        .job-row { display: flex; justify-content: space-between; align-items: center; padding: 14px 0; border-bottom: 1px solid #f1f3f5; gap: 12px; }
        .job-row:last-child { border-bottom: none; }
        .job-initials { width: 42px; height: 42px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; flex-shrink: 0; }
        .status-pill { display: inline-flex; align-items: center; gap: 4px; border-radius: 20px; font-size: 11px; font-weight: 600; padding: 3px 10px; text-transform: capitalize; }
        .profile-info-row { display: flex; justify-content: space-between; padding: 9px 0; border-bottom: 1px solid #f8f9fa; font-size: 13.5px; }
        .profile-info-row:last-child { border-bottom: none; }
        .pir-label { color: #868e96; font-weight: 500; }
        .pir-value { color: #212529; font-weight: 500; text-align: right; max-width: 60%; }
        .company-avatar { width: 64px; height: 64px; border-radius: 16px; background: linear-gradient(135deg, #0d6efd, #6f42c1); display: flex; align-items: center; justify-content: center; color: #fff; font-size: 22px; font-weight: 700; margin-bottom: 12px; }
      `}</style>

      <Sidebar />
      <main className="content">
        <Navbar />

        {/* Header */}
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h4 className="fw-bold mb-1" style={{ letterSpacing: "-0.3px" }}>
                <i className="bi bi-speedometer2 text-primary me-2" />
                Recruiter Dashboard
              </h4>
              <p className="text-muted mb-0 small">Manage job posts, track applicants, and grow your team.</p>
            </div>
            <div className="d-flex gap-2">
              <Link to="/recruiter/plans" className="btn btn-outline-dark">
                <i className="bi bi-credit-card me-2" />My Plan
              </Link>
              <Link to="/recruiter/profile" className="btn btn-outline-primary">
                <i className="bi bi-building me-2" />Company Profile
              </Link>
              <Link
                to={subscription?.status === "active" ? "/recruiter/jobs/new" : "/recruiter/plans"}
                className={`btn ${subscription?.status === "active" ? "btn-primary" : "btn-secondary"}`}
              >
                <i className="bi bi-plus-lg me-2" />
                {subscription?.status === "active" ? "Post a Job" : "Buy Plan to Post"}
              </Link>
            </div>
          </div>
        </div>

        {!loading && subscription?.status !== "active" && (
          <div className="alert alert-warning border-0 shadow-sm">
            Your recruiter plan is inactive. Buy a plan to continue posting jobs.
          </div>
        )}

        {/* Metrics */}
        <div className="row g-3 mb-4">
          {METRIC_CONFIG.map((m) => (
            <div className="col-md-6 col-xl" key={m.key}>
              <div className="dash-metric-card shadow-sm" style={{ background: m.bg }}>
                {loading ? (
                  <div className="placeholder-glow">
                    <span className="placeholder rounded mb-3 d-block" style={{ width: 44, height: 44 }} />
                    <span className="placeholder col-5 rounded d-block mb-2" style={{ height: 28 }} />
                    <span className="placeholder col-8 rounded d-block" style={{ height: 12 }} />
                  </div>
                ) : (
                  <>
                    <div className="dash-metric-icon" style={{ background: `${m.color}1a`, color: m.color }}>
                      <i className={`bi ${m.icon}`} />
                    </div>
                    <div className="dash-metric-val" style={{ color: m.color }}>{metrics[m.key]}</div>
                    <div className="dash-metric-label">{m.label}</div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {!loading && (
          <div className="row g-4">
            {/* Recent Jobs */}
            <div className="col-lg-7">
              <div className="card section-card border-0 h-100">
                <div className="card-body">
                  <div className="section-header pt-0">
                    <h2 className="section-title">
                      <i className="bi bi-briefcase text-primary me-2" />Recent Jobs
                    </h2>
                    <Link to="/recruiter/jobs" className="btn btn-sm btn-outline-primary" style={{ borderRadius: 8 }}>
                      View all
                    </Link>
                  </div>

                  {recentJobs.length === 0 ? (
                    <div className="text-center py-4">
                      <i className="bi bi-briefcase text-muted" style={{ fontSize: 40, opacity: 0.25 }} />
                      <p className="text-muted small mt-3 mb-3">No jobs posted yet.</p>
                      <Link to="/recruiter/jobs/new" className="btn btn-sm btn-primary" style={{ borderRadius: 8 }}>
                        <i className="bi bi-plus me-1" />Post your first job
                      </Link>
                    </div>
                  ) : (
                    recentJobs.map((job) => {
                      const initials = (job.company?.name || profile?.username || "CO").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
                      const colors = [["#e7f1ff","#0d6efd"],["#f0ebff","#6f42c1"],["#e6f4ed","#198754"],["#fff3e0","#fd7e14"],["#fce8f3","#c2185b"]];
                      const [cbg, cfg] = colors[(job.title?.charCodeAt(0) || 0) % colors.length];
                      const statusStyle = STATUS_STYLES[job.jobStatus] || STATUS_STYLES.active;

                      return (
                        <div className="job-row" key={job.id}>
                          <div className="d-flex gap-3 align-items-center">
                            <div className="job-initials" style={{ background: cbg, color: cfg }}>{initials}</div>
                            <div>
                              <div className="fw-semibold" style={{ fontSize: 14 }}>{job.title}</div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                <i className="bi bi-geo-alt me-1" />{job.location?.display || "Location pending"}
                              </div>
                              <div className="d-flex align-items-center gap-2 mt-1">
                                <span className="status-pill" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor", display: "inline-block" }} />
                                  {job.jobStatus || "active"}
                                </span>
                                <span className="text-muted" style={{ fontSize: 11 }}>
                                  <i className="bi bi-people me-1" />{job.applicationCount || 0} applicants
                                </span>
                              </div>
                            </div>
                          </div>
                          <Link to={`/recruiter/jobs/${job.id}/applicants`} className="btn btn-sm btn-outline-primary flex-shrink-0" style={{ borderRadius: 8 }}>
                            <i className="bi bi-people me-1" />Applicants
                          </Link>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Company Profile */}
            <div className="col-lg-5">
              <div className="card section-card border-0 h-100">
                <div className="card-body">
                  <div className="section-header pt-0">
                    <h2 className="section-title">
                      <i className="bi bi-building text-primary me-2" />Company Profile
                    </h2>
                    <Link to="/recruiter/profile" className="btn btn-sm btn-outline-primary" style={{ borderRadius: 8 }}>
                      <i className="bi bi-pencil me-1" />Update
                    </Link>
                  </div>

                  <div className="company-avatar">
                    {(profile?.username || "R").charAt(0).toUpperCase()}
                  </div>

                  <div className="fw-bold mb-1" style={{ fontSize: 16 }}>
                    {profile?.username || "Recruiter Account"}
                  </div>
                  <div className="text-muted small mb-3">
                    <i className="bi bi-envelope me-1" />{profile?.email || "No email"}
                    {profile?.phone && <><span className="mx-2">·</span><i className="bi bi-telephone me-1" />{profile.phone}</>}
                  </div>

                  {[
                    { label: "Website", value: profile?.website, icon: "bi-globe" },
                    { label: "Industry", value: industryList, icon: "bi-diagram-3" },
                    { label: "Location", value: [profile?.city, profile?.statename].filter(Boolean).join(", ") || null, icon: "bi-geo-alt" },
                  ].map(({ label, value, icon }) => (
                    <div className="profile-info-row" key={label}>
                      <span className="pir-label"><i className={`bi ${icon} me-2`} />{label}</span>
                      <span className="pir-value" style={{ color: value ? "#212529" : "#ced4da" }}>{value || "Not added"}</span>
                    </div>
                  ))}

                  {profile?.about && (
                    <div className="mt-3 p-3 rounded-3" style={{ background: "#f8f9fa", fontSize: 13 }}>
                      <div className="text-muted small fw-semibold mb-1">ABOUT</div>
                      <p className="mb-0 text-muted" style={{ lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {profile.about}
                      </p>
                    </div>
                  )}

                  <div className="d-flex gap-2 mt-3">
                    <Link to="/recruiter/jobs/new" className="btn btn-primary btn-sm flex-grow-1" style={{ borderRadius: 8 }}>
                      <i className="bi bi-plus-lg me-1" />Create Job
                    </Link>
                    <Link to="/recruiter/jobs" className="btn btn-outline-dark btn-sm flex-grow-1" style={{ borderRadius: 8 }}>
                      <i className="bi bi-grid me-1" />Manage Jobs
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default RecruiterDashboard;
